import React, { useEffect, useState } from 'react';
import { Divider, Table, Tooltip, Space, Button, Dropdown } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateWebhookStatus, deleteWebhook } from '@api/notificationChannel';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  CheckCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { IWebhook } from '../types';
import { StyledDisabledField, StyledWebhookUrl } from '../styled';
import { getFilterWebhookList } from '../utils';
import { activeFilterAtom, currentWebhookRowAtom, showHistoryAtom } from '../webhooksAtom';
import WebhooksChangeHistoryLog from '../History';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { useSelector } from 'react-redux';

const { baseUrl } = window.conviva;

const WebhooksList: React.FC<{
  webhookList: IWebhook[];
}> = ({ webhookList }) => {
  const { context } = window.conviva;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentWebhookList, setCurrentWebhookList] = useState(webhookList);
  const activeFilter = useAtomValue(activeFilterAtom);
  const setShowHistory = useSetAtom(showHistoryAtom);
  const setCurrentWebhookRow = useSetAtom(currentWebhookRowAtom);
  const isAdminUser = useSelector(isAdminRoleSelector);

  useEffect(() => {
    const filteredWebhookList = getFilterWebhookList(webhookList, activeFilter);
    setCurrentWebhookList(filteredWebhookList);
  }, [webhookList, activeFilter]);

  const updateWebhookStatusMutation = useMutation(updateWebhookStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['webhooksListData']);
    },
  });

  const deleteWebhookMutation = useMutation(deleteWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries(['webhooksListData']);
    },
  });

  const menu = (record) => {
    const showDisableButton = !record.error && !record.status;
    const showEnableButton = !record.error && record.status;
    const showHistoryButton = !record.error;

    const result: any = {
      items: [],
    };

    if (showDisableButton && isAdminUser) {
      result.items.push({
        key: 'enable',
        label: (
          <a onClick={() => handleEnable(record)}>
            <CheckCircleOutlined /> Enable
          </a>
        ),
      });
    }

    if (showEnableButton && isAdminUser) {
      result.items.push({
        key: 'disable',
        label: (
          <a onClick={() => handleDisable(record)}>
            <StopOutlined /> Disable
          </a>
        ),
      });
    }

    if (isAdminUser) {
      result.items.push({
        key: 'edit',
        label: (
          <a onClick={() => handleEdit(record)}>
            <EditOutlined /> Edit
          </a>
        ),
      });
    } else {
      result.items.push({
        key: 'view',
        label: (
          <a onClick={() => handleEdit(record)}>
            <EditOutlined /> View
          </a>
        ),
      });
    }

    if (isAdminUser) {
      result.items.push({
        key: 'delete',
        label: (
          <a onClick={() => handleDelete(record)}>
            <DeleteOutlined /> Delete
          </a>
        ),
      });
    }
    if (showHistoryButton) {
      result.items.push({
        key: 'history',
        label: (
          <a onClick={() => handleShowHistory(record)}>
            <HistoryOutlined /> History
          </a>
        ),
      });
    }

    return result;
  };

  const columns: ColumnsType<IWebhook> = [
    {
      title: 'Webhooks Name',
      dataIndex: 'name',
      render: (name: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return <StyledDisabledField>{name}</StyledDisabledField>;
        } else {
          return <div>{name}</div>;
        }
      },
    },
    {
      title: 'URL',
      dataIndex: 'webhookUrl',
      render: (webhookUrl: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return (
            <StyledDisabledField>
              <StyledWebhookUrl>{webhookUrl}</StyledWebhookUrl>
            </StyledDisabledField>
          );
        } else {
          return <StyledWebhookUrl>{webhookUrl}</StyledWebhookUrl>;
        }
      },
    },
    {
      title: 'Modified Date & Time',
      dataIndex: 'updatedAt',
      defaultSortOrder: 'descend',
      // reference https://github.com/microsoft/TypeScript/issues/5710#issuecomment-157886246
      sorter: (a: IWebhook, b: IWebhook) => +new Date(a.updatedAt) - +new Date(b.updatedAt),
      render: (updatedAt: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return (
            <StyledDisabledField>
              {moment(updatedAt).format('DD  MMM, YYYY HH:mm')}
            </StyledDisabledField>
          );
        } else {
          return <div>{moment(updatedAt).format('DD  MMM, YYYY HH:mm')}</div>;
        }
      },
    },
    context !== 'app'
      ? {
          title: 'Alert Type',
          dataIndex: 'alertType',
          align: 'center',
          render: (alertType: string, record: IWebhook) => {
            if (record.error || !record.status) {
              return <StyledDisabledField>{alertType}</StyledDisabledField>;
            } else {
              return <div>{alertType}</div>;
            }
          },
        }
      : {},
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string, record: IWebhook) => {
        if (record.error) {
          return (
            <Tooltip title={record.errorDetail} placement="top">
              <div style={{ color: 'red' }}>Error</div>
            </Tooltip>
          );
        } else {
          if (record.status) {
            return <div style={{ color: 'green' }}>Enabled</div>;
          } else {
            return <div style={{ color: 'grey' }}>Disabled</div>;
          }
        }
      },
    },
    {
      title: '',
      dataIndex: 'action',
      align: 'center',
      render: (text: string, record: IWebhook) => {
        
        return (
          <Space size="middle">
            <Dropdown menu={menu(record)} trigger={['click']} placement="bottomRight">
              <Button type="link">
                <MoreOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const handleEdit = (record) => {
    navigate(`${baseUrl}/settings/webhooks/edit/${record.webhookId}`);
  };

  const handleEnable = (record) => {
    updateWebhookStatusMutation.mutate({
      id: Number(record.webhookId),
      status: 1,
    });
  };

  const handleDisable = (record) => {
    updateWebhookStatusMutation.mutate({
      id: Number(record.webhookId),
      status: 0,
    });
  };

  const handleDelete = (record) => {
    deleteWebhookMutation.mutate({
      id: Number(record.webhookId),
    });
  };

  const handleShowHistory = (record) => {
    setCurrentWebhookRow(record);
    setShowHistory(true);
  };

  return (
    <div>
      <Divider />
      <Table
        columns={columns}
        dataSource={currentWebhookList}
        rowKey={(record) => record.webhookId}
      />
      <WebhooksChangeHistoryLog />
    </div>
  );
};

export default WebhooksList;
